import { useEffect, useState } from "react";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";

import { useRetentionDetail, type KpiMonthRange, type TierBasis } from "../../kpi/api";
import {
  RetentionDetailBody,
  retentionSubtitle,
} from "../../kpi/components/RetentionDrawer";

/** A tier band the Retention matrix can drill into. `tiers = []` means All. */
export interface RetentionSegment {
  key: string;
  label: string;
  tiers: number[];
}

/**
 * Reuses the KPI $ Retention drill-down — the same NRR/GDR chart, filters, and
 * per-entity table — opened from a Customer Tiers → Retention matrix cell,
 * pre-filtered to the clicked tier segment. Once open it behaves exactly like
 * the KPI card's expanded view: the user can re-slice tiers, change basis, or
 * collapse to the quick view. Always mounted; `segment` drives open/close.
 */
export function TierRetentionDrawer({
  segment,
  range,
  onClose,
}: {
  segment: RetentionSegment | null;
  range: KpiMonthRange;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [tierBasis, setTierBasis] = useState<TierBasis>("base");
  const [tiers, setTiers] = useState<number[]>([]);

  // Each matrix click re-seeds the tier filter and reopens in the full view.
  useEffect(() => {
    if (segment) {
      setTiers(segment.tiers);
      setTierBasis("base");
      setExpanded(true);
    }
  }, [segment]);

  const open = segment != null;
  const detail = useRetentionDetail("CUSTOM", range, tierBasis, tiers, open);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={setExpanded}
      onClose={onClose}
      title={`$ Retention — ${segment?.label ?? "All"}`}
      subtitle={retentionSubtitle(detail.data?.anchor_month)}
    >
      <RetentionDetailBody
        detail={detail}
        expanded={expanded}
        tierBasis={tierBasis}
        setTierBasis={setTierBasis}
        tiers={tiers}
        setTiers={setTiers}
      />
    </EntityDrawer>
  );
}
